<!-- AppToolbar.vue -->
<template>
  <v-app-bar color="primary">
    <v-app-bar-title>Be-Housing Guest App</v-app-bar-title>

    <v-tabs
      v-if="showTopNavItems"
      :model-value="routeName"
      align-tabs="center"
      class="guest-tabs"
    >
      <v-tab
        v-for="item in visibleItems"
        :key="item.route"
        :value="item.route"
        @click="go(item.route)"
      >
        <v-icon start :icon="item.icon" />
        {{ item.title }}
      </v-tab>
    </v-tabs>

    <template #append>
      <v-btn
        v-if="isGuest"
        icon="mdi-qrcode"
        :loading="qrStore.qrLoading"
        :disabled="qrStore.qrLoading"
        @click="qrStore.openQrDialog"
      />

      <v-menu>
        <template #activator="{ props }">
          <v-btn icon="mdi-web" v-bind="props" />
        </template>

        <v-list>
          <v-list-item
            v-for="language in languages"
            :key="language.code"
            @click="changeLanguage(language.code)"
          >
            <v-list-item-title>{{ language.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu
        v-model="accountMenuOpen"
        location="bottom end"
        width="220"
      >
        <template #activator="{ props }">
          <v-btn
            icon="mdi-dots-vertical"
            aria-label="Menu"
            v-bind="props"
          />
        </template>

        <v-list>
          <v-list-item prepend-icon="mdi-logout" @click="logOut">
            <v-list-item-title>
              {{ GlobalService.translator().trans("general.menu.log_out") }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-app-bar>

  <v-dialog v-model="qrStore.qrDialogOpen" max-width="360">
    <v-card rounded="xl">
      <v-card-title>
        {{ GlobalService.translator().trans("reservation.qr_code.title") }}
      </v-card-title>
      <v-card-text>
        <div v-if="qrStore.qrLoading" class="qr-dialog__loading">
          <v-progress-circular indeterminate />
        </div>

        <div v-else-if="qrStore.qrCodeUrl" class="qr-dialog__content">
          <img :src="qrStore.qrCodeUrl" :alt="GlobalService.translator().trans('reservation.qr_code.title')" />
          <div class="qr-dialog__hint">
            {{ GlobalService.translator().trans("reservation.qr_code.hint") }}
          </div>
        </div>

        <v-alert v-else type="info" variant="tonal" density="compact">
          {{ GlobalService.translator().trans("reservation.qr_code.not_available") }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="qrStore.closeQrDialog">
          {{ GlobalService.translator().trans("general.login.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import GlobalService from "@/services/global-service";
import useStore from "@/stores";
import { useAppNav } from "@/composables/useAppNav";
import { nextTick, onBeforeUnmount, ref } from "vue";

const accountMenuOpen = ref(false);

async function logOut(): Promise<void> {
  accountMenuOpen.value = false;
  await nextTick();
  await session.reAuth(true);
}
const { showTopNavItems, isGuest, routeName, visibleItems } = useAppNav();

const store = useStore();
const session = store.session;
const qrStore = store.qr;

const languages = [
  { code: "en", name: "English" },
  { code: "nl", name: "Nederlands" },
  { code: "fr", name: "Français" }
];

async function go(name: string): Promise<void> {
  await GlobalService.router().routeTo(name);
}

function changeLanguage(language: string): void {
  session.language = language;
  GlobalService.translator().setLanguage(language);
}

onBeforeUnmount((): void => {
  qrStore.revokeQrCodeUrl();
});
</script>
<style scoped lang="scss">
.guest-tabs {
  max-width: 47.5rem;
  margin: 0 auto;
}

.qr-dialog__title {
  font-weight: 800;
  font-size: 1rem;
}

.qr-dialog__loading {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-dialog__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr-dialog__image {
  width: 220px;
  max-width: 100%;
  height: auto;
  border-radius: 12px;
}

.qr-dialog__hint {
  text-align: center;
  font-size: 0.85rem;
  color: rgba(var(--v-theme-on-surface), 0.68);
}
</style>
